// @ts-nocheck
/**
 * Exam Center — Admin route shell.
 *
 * The page is split into three role-gated tabs (Builder, Monitor, Grading),
 * each in its own file under `./exam-center/`. Builder is open to anyone with
 * manage:courses (ContentCreators included); Monitor and Grading are reserved
 * for System/Super Administrators. This file is the route entry point — it
 * handles the TanStack Router declaration, permission checks, and tab layout
 * wiring.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/use-auth";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { ShieldCheck, Settings, BarChart3, UserPlus } from "lucide-react";

import { BuilderTab } from "./exam-center/BuilderTab";
import { MonitorTab } from "./exam-center/MonitorTab";
import { GradingTab } from "./exam-center/GradingTab";

const VALID_TABS = ["builder", "monitor", "assignments"] as const;

export const Route = createFileRoute("/_authenticated/admin/exam-center")({
  validateSearch: (search: Record<string, unknown>): { tab?: string; examId?: string } => ({
    tab:
      typeof search.tab === "string" && (VALID_TABS as readonly string[]).includes(search.tab)
        ? search.tab
        : undefined,
    examId: typeof search.examId === "string" ? search.examId : undefined,
  }),
  component: ExamCenterPage,
});

function ExamCenterPage() {
  const { hasPermission, isAdmin } = useAuth();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  // Monitor and Grading are administration surfaces: only a System/Super Administrator gets them.
  // `isAdmin` is true for both (hasAnyRole normalizes SuperAdmin to SystemAdmin), and is checked in
  // addition to the permission so a ContentCreator can never reach proctoring or certification
  // grading here, however their permissions are composed. Their grading path is /team-leader/exams.
  const canOversee = isAdmin;

  if (!hasPermission("manage:courses") && !canOversee) {
    return <AccessDenied message="You don't have permission to access the Exam Center." />;
  }

  // Role-adaptive tabs: ContentCreators build; only administrators monitor and grade.
  const tabs = [
    hasPermission("manage:courses") && {
      value: "builder",
      label: "Builder",
      Icon: Settings,
      render: () => <BuilderTab examId={search.examId} />,
    },
    canOversee &&
      hasPermission("view:analytics") && {
        value: "monitor",
        label: "Monitor",
        Icon: BarChart3,
        render: () => <MonitorTab />,
      },
    canOversee &&
      hasPermission("grade:submissions") && {
        value: "assignments",
        label: "Grading",
        Icon: UserPlus,
        render: () => <GradingTab />,
      },
  ].filter(Boolean) as { value: string; label: string; Icon: any; render: () => any }[];

  const activeTab =
    search.tab && tabs.some((t) => t.value === search.tab) ? search.tab : tabs[0]?.value;

  const colsClass =
    { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3" }[tabs.length] || "grid-cols-1";

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto pb-12">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <ShieldCheck className="h-8 w-8 text-primary" />
            Exam Center
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Global standard testing, proctoring, and certification grading.
          </p>
        </div>
      </div>

      {tabs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            You don't have access to any Exam Center tools.
          </CardContent>
        </Card>
      ) : (
        <Tabs
          value={activeTab}
          onValueChange={(next) =>
            navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true })
          }
          className="space-y-6"
        >
          <TabsList className={`grid w-full max-w-md ${colsClass}`}>
            {tabs.map((t) => (
              <TabsTrigger key={t.value} value={t.value} className="gap-2">
                <t.Icon className="h-4 w-4" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs.map((t) => (
            <TabsContent key={t.value} value={t.value}>
              {t.render()}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
